<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';

const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const conversations = computed(() => store.getters.getAllConversations || []);

const CHANNEL_META = {
  'Channel::WebWidget': { label: 'Web', color: '#9B59F6' },
  'Channel::Whatsapp': { label: 'WhatsApp', color: '#25D366' },
  'Channel::Email': { label: 'Email', color: '#0066FF' },
  'Channel::FacebookPage': { label: 'Facebook', color: '#1877F2' },
  'Channel::TwilioSms': { label: 'SMS', color: '#F22F46' },
  'Channel::Telegram': { label: 'Telegram', color: '#26A5E4' },
  'Channel::Sms': { label: 'SMS', color: '#F22F46' },
  'Channel::Line': { label: 'LINE', color: '#06C755' },
  'Channel::Api': { label: 'API', color: '#1A1A2E' },
};
const OTHER = { label: 'Other', color: '#94A3B8' };

const inboxChannelMap = computed(() =>
  (inboxes.value || []).reduce((acc, inbox) => {
    acc[inbox.id] = inbox.channel_type;
    return acc;
  }, {})
);

const buckets = computed(() =>
  conversations.value.reduce((acc, c) => {
    const channel = inboxChannelMap.value[c.inbox_id] || 'unknown';
    acc[channel] = (acc[channel] || 0) + 1;
    return acc;
  }, {})
);

const total = computed(() =>
  Object.values(buckets.value).reduce((a, b) => a + b, 0)
);

const segments = computed(() => {
  if (total.value === 0) return [];
  return Object.entries(buckets.value)
    .map(([channel, count]) => {
      const meta = CHANNEL_META[channel] || OTHER;
      return {
        channel,
        label: meta.label,
        color: meta.color,
        count,
        pct: (count / total.value) * 100,
      };
    })
    .sort((a, b) => b.count - a.count);
});
</script>

<template>
  <div v-if="total > 0" dir="auto" class="px-3 py-2 border-t border-n-weak">
    <div class="flex items-center justify-between mb-1.5">
      <span
        class="text-xxs font-medium text-n-slate-11 uppercase tracking-wide"
      >
        {{ $t('SMARTICKS.CHANNEL_MIX.TITLE') }}
      </span>
      <span class="text-xxs text-n-slate-11">{{ total }}</span>
    </div>
    <div class="flex w-full h-1.5 rounded-full overflow-hidden bg-n-slate-3">
      <div
        v-for="seg in segments"
        :key="seg.channel"
        :style="{ width: `${seg.pct}%`, backgroundColor: seg.color }"
        :title="`${seg.label}: ${seg.count} (${seg.pct.toFixed(0)}%)`"
        class="h-full transition-all duration-300"
      />
    </div>
    <ul class="flex flex-wrap gap-x-2 gap-y-1 mt-2 text-xxs text-n-slate-11">
      <li
        v-for="seg in segments"
        :key="`legend-${seg.channel}`"
        class="flex items-center gap-1"
      >
        <span
          class="inline-block w-1.5 h-1.5 rounded-full"
          :style="{ backgroundColor: seg.color }"
        />
        <span>{{ seg.label }}</span>
        <span class="text-n-slate-10">{{ seg.count }}</span>
      </li>
    </ul>
  </div>
</template>
